<template>
    <div class="ufo-sepa--head">
        <div class="head-control">

            <Cta :type="'icon xl back subtle no-bg'" :dark-scheme="true" v-if="showBack && !cascadingView" @click="back()" >
                <Icon icon-name="back"/>
            </Cta>

            <div class="head-title-container">
                <div class="head-title-row">
                    <div class="ufo-sepa--title">{{ title }}</div>
                    <template v-if="showContext">
                        <Icon class="head-title-separator" icon-name="chevron-right" :width="20" :height="20" />
                        <div class="ufo-sepa--title head-title-context">{{ contextTitle }}</div>
                    </template>
                </div>
                <div v-if="showCaseStudySubtitle" class="head-subtitle">
                    <Icon :height="16" :width="16" :icon-name="'page'" />
                    <span class="head-subtitle-text">{{ caseStudyTitle }}</span>
                </div>
            </div>
            
            <div class="head-control-actions">
                <div
                    v-if="showGlobalToggle"
                    ref="globalToggleRef"
                    class="color-toggle-btn"
                    @mouseenter="hovering = true"
                    @mouseleave="hovering = false"
                >
                    <Cta
                        :type="'icon xl subtle no-bg'"
                        :dark-scheme="true"
                        @click="toggleGlobalSettingForPanel()"
                    >
                        <Icon :icon-name="displayIcon"/>
                    </Cta>

                    <SmallTooltip
                        v-if="hovering"
                        :text="globalToggleTooltip"
                        :target-element="$refs.globalToggleRef"
                    />
                </div>
                
                <Cta :type="'icon xl close subtle no-bg'" :dark-scheme="true" @click="close()">
                    <Icon icon-name="close"/>
                </Cta>
            </div>

        </div>
        <div v-if="settings.tabPills" class="head-tabs">
            <TabBar dark-scheme hide-border>
                <TabItem
                    v-for="tab in settings.tabPills"
                    :key="tab.type"
                    @click="selectTab(tab.type)"
                    :active="settings.activeTab === tab.type"
                    dark-scheme
                    :label="tab.label"
                />
            </TabBar>
        </div>
    </div>
</template>

<script>
    import Cta from "../../components/CtaButton.vue";

    import Icon from "../../Icon.vue";
    import TabItem from "../../components/TabItem.vue";
    import TabBar from "../../components/TabBar.vue";
    import {CtaTypesEnum, DesignPanelTypesEnum} from "../../../common/enums.js";
    import {convertRichTextHTMLToPlainText} from "@/scripts/common/components/textEditor/utilities/textConverterUtilities.js";

    export default {
        name: 'panel-header',
        props: {
            cascadingView: {
                type: Boolean,
                default: false
            },
            showCaseStudyContext: {
                type: Boolean,
                default: false
            },
            showPortfolioContext: {
                type: Boolean,
                default: false
            }
        },
        components: {TabBar, TabItem, Icon, Cta},
        computed: {
            settings() {
                return this.$store.state.sidePanelSettings;
            },
            title () {
                return this.settings.title === "Edit buttons" ? `Edit ${this.settings.editedCtaType ?? CtaTypesEnum.PRIMARY} buttons` : this.settings.title;
            },
            showContext() {
                return !this.cascadingView
                    && this.settings.panel === DesignPanelTypesEnum.MAIN_PANEL
                    && ((this.showCaseStudyContext && this.$store.getters.isCaseStudy)
                        || (this.showPortfolioContext && !this.$store.getters.isCaseStudy));
            },
            contextTitle() {
                return this.$store.getters.isCaseStudy ? 'Case study' : 'Portfolio';
            },
            showCaseStudySubtitle() {
                return this.showContext && this.$store.getters.isCaseStudy;
            },
            caseStudyTitle() {
                const caseStudy = this.$store.state.project;
                let headerTitle = caseStudy.sections[0];
                return caseStudy.name !== null
                    ? caseStudy.name
                    : convertRichTextHTMLToPlainText(headerTitle.title.text)
            },
            showBack() {
                return !this.settings.hideBackButton
            },
            hasUnsavedChanges() {
                return this.settings.allowSave;
            }
        },
        methods: {
            back() {
                if (this.hasUnsavedChanges) {
                    this.$store.commit("updateSidePanel", { activeModal: "CancelConfirmModal" });
                } else {
                    this.$emit('back');
                }
            },
            close() {
                if (this.hasUnsavedChanges) {
                    this.$store.commit("updateSidePanel", { activeModal: "CancelConfirmModal" });
                } else {
                    this.$emit('close');
                }
            },
            selectTab(tabType) {
                this.$store.commit("updateSidePanel", {
                    activeTab: tabType
                })
            }
        }
    }
</script>

<style lang="scss" scoped>
    .button-icon svg {
        width: 24px;
        height: 24px;
    }

    .head-tabs {
        margin-top: -16px;
        padding: 0 30px;
    }

    .head-title-container {
        min-width: 0;
        margin-right: auto;
    }

    .head-title-row {
        display: flex;
        align-items: center;
        gap: 8px;
        min-width: 0;

        .ufo-sepa--title {
            margin-right: 0;
        }
    }

    .head-title-context,
    .head-title-separator {
        flex: 0 0 auto;
        color: var(--asphalt50);
    }

    .head-subtitle {
        display: flex;
        gap: 4px;
        align-items: center;
        margin-top: 8px;
        font: var(--editor-ui-b3-regular);
        line-height: 15px;
        color: var(--asphalt50);
        min-width: 0;
        user-select: none;

        svg {
            flex: 0 0 auto;
        }

        .head-subtitle-text {
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }

    .head-control-actions {
        display: flex;
        align-items: center;
        gap: 8px;
    }

</style>
